import * as React from 'react';

import { Unit, enumUnit, Weight } from '@rematter/frontend-lib-api-client';
import {
  calculateLineItemsTotal,
  convertWeightForPricing,
} from '@rematter/modules/trade/utils/weight';
import { convertFromPounds, weightToPounds } from '@rematter/ui-web';
import Big from 'big.js';
import partition from 'lodash/partition';

import {
  FreightChargeForm,
  ReceivedLineItemForm,
  ReconciliationDeductionForm,
} from '~/modules/trade/_state/loadReconciliation.state';
import {
  enumLoadWeightType,
  LoadFormLineItem,
  WeightUnitType,
} from '~/modules/trade/types/load';

type Props = {
  shippedLineItems: LoadFormLineItem[];
  receivedLineItems: ReceivedLineItemForm[];
  deductions: ReconciliationDeductionForm[];
  freightCharges: FreightChargeForm[];
  weightUnit: WeightUnitType;
  facilityDefaultUnit: Unit;
};

export type FormulaValue = {
  weight?: Weight;
  each?: number;
};

export type ReconciliationSummary = {
  // Monetary totals
  shippedLoadTotal: number;
  receivedLoadTotal: number;
  totalDeductionsAmount: number;
  moneyDelta: number;

  // Formula breakdowns
  netShipped: FormulaValue;
  netReceived: FormulaValue;
  deduction: FormulaValue;
  delta: FormulaValue;
};

export const useReconciliationSummary = ({
  shippedLineItems,
  receivedLineItems,
  deductions,
  freightCharges,
  weightUnit,
  facilityDefaultUnit,
}: Props): ReconciliationSummary =>
  React.useMemo(() => {
    // Determine display unit based on weight unit toggle and pricing unit
    const getDisplayUnit = (pricingUnit: Unit | null | undefined): Unit => {
      const currentPricingUnit = pricingUnit || enumUnit.Pounds;

      // Exception: "Each" always displays as "Each" regardless of toggle
      if (currentPricingUnit === enumUnit.Each) return enumUnit.Each;

      // Otherwise respect the toggle: 'price' uses pricing unit, 'scale' uses facility default
      return weightUnit === 'price' ? currentPricingUnit : facilityDefaultUnit;
    };

    // Determine common pricing unit from load line items
    const [_eachItems, weightItems] = partition(
      shippedLineItems,
      (item) => item.pricingUnit === 'Each',
    );

    // Check if all weight items share the same pricing unit
    const samePricingUnit =
      weightItems.length > 0 &&
      weightItems.every(
        (item) => item.pricingUnit === weightItems[0]?.pricingUnit,
      );

    const commonPricingUnit = samePricingUnit
      ? (weightItems[0]?.pricingUnit as Unit)
      : enumUnit.Pounds;

    // Calculate display unit
    const displayUnit = getDisplayUnit(commonPricingUnit);

    // Separate shipped items by type
    const [shippedEachItems, shippedWeightItems] = partition(
      shippedLineItems,
      (item) => item.pricingUnit === 'Each',
    );

    // Calculate shipped weight totals (excluding Each items)
    const netShippedWeightPounds = shippedWeightItems.reduce(
      (sum, li) => sum + weightToPounds(li.weights?.[enumLoadWeightType.Net]),
      0,
    );
    const netShippedWeight = convertFromPounds(
      netShippedWeightPounds,
      displayUnit,
    );

    // Calculate shipped Each count
    const netShippedEach = shippedEachItems.reduce(
      (sum, li) =>
        sum + (Number(li.weights?.[enumLoadWeightType.Net]?.value) || 0),
      0,
    );
    // Calculate shipped monetary totals (all items)
    const shippedLoadTotal = calculateLineItemsTotal(shippedLineItems);

    // Separate received items by type
    const [receivedEachItems, receivedWeightItems] = partition(
      receivedLineItems,
      (item) => item.pricingUnit === 'Each',
    );

    // Calculate received weight totals (excluding Each items)
    const netReceivedWeightPounds = receivedWeightItems.reduce(
      (sum, item) =>
        sum +
        (weightToPounds(item.grossWeight) - weightToPounds(item.tareWeight)),
      0,
    );
    const netReceivedWeight = convertFromPounds(
      netReceivedWeightPounds,
      displayUnit,
    );

    // Calculate received Each count
    const netReceivedEach = receivedEachItems
      .reduce(
        (sum, item) =>
          sum.plus(
            new Big(item.grossWeight?.value ?? 0).minus(
              item.tareWeight?.value ?? 0,
            ),
          ),
        new Big(0),
      )
      .toNumber();

    // Calculate price total using proper unit conversion for pricing
    const receivedLoadTotal = receivedLineItems.reduce((sum, item) => {
      const netWeightPounds =
        weightToPounds(item.grossWeight) - weightToPounds(item.tareWeight);
      const pricingUnit = item.pricingUnit ?? enumUnit.Pounds;
      const convertedWeight = convertWeightForPricing(
        netWeightPounds,
        pricingUnit,
      );
      return sum + (item.unitPrice?.amount ?? 0) * convertedWeight;
    }, 0);

    // Calculate deductions totals
    const deductionsPriceTotal = deductions.reduce(
      (sum, deduction) => sum + (deduction.priceQuantity?.amount ?? 0),
      0,
    );

    // Separate deductions by type (assuming deductions follow the same unit system)
    const deductionsWeightTotalPounds = deductions
      .filter((d) => d.weightQuantity?.unit !== 'Each')
      .reduce(
        (sum, deduction) => sum + weightToPounds(deduction.weightQuantity),
        0,
      );
    const deductionsWeightTotal = convertFromPounds(
      deductionsWeightTotalPounds,
      displayUnit,
    );

    const deductionsEachTotal = deductions
      .filter((d) => d.weightQuantity?.unit === 'Each')
      .reduce(
        (sum, deduction) => sum + Number(deduction.weightQuantity?.value ?? 0),
        0,
      );

    // Calculate freight charges total (net amount = accrued - chargeback)
    const freightChargesTotal = freightCharges.reduce(
      (sum, charge) => sum + Number(charge.chargebackAmount?.amount ?? 0),
      0,
    );

    const totalDeductionsAmount = deductionsPriceTotal + freightChargesTotal;

    // Calculate deltas for weight items
    const weightDelta =
      netShippedWeight - netReceivedWeight - deductionsWeightTotal;

    // Calculate deltas for Each items
    const eachDelta = netShippedEach - netReceivedEach - deductionsEachTotal;

    // Calculate monetary delta
    const moneyDelta =
      shippedLoadTotal - receivedLoadTotal - totalDeductionsAmount;

    const hasWeightItems =
      shippedWeightItems.length > 0 || receivedWeightItems.length > 0;
    const hasEachItems =
      shippedEachItems.length > 0 || receivedEachItems.length > 0;

    return {
      // Monetary totals
      shippedLoadTotal,
      receivedLoadTotal,
      totalDeductionsAmount,
      moneyDelta,

      // Formula breakdowns
      netShipped: {
        weight: hasWeightItems
          ? {
              value: netShippedWeight,
              unit: displayUnit,
              __typename: 'Weight',
            }
          : undefined,
        each: hasEachItems ? netShippedEach : undefined,
      },
      netReceived: {
        weight: hasWeightItems
          ? {
              value: netReceivedWeight,
              unit: displayUnit,
              __typename: 'Weight',
            }
          : undefined,
        each: hasEachItems ? netReceivedEach : undefined,
      },
      deduction: {
        weight: hasWeightItems
          ? {
              value: deductionsWeightTotal,
              unit: displayUnit,
              __typename: 'Weight',
            }
          : undefined,
        each: hasEachItems ? deductionsEachTotal : undefined,
      },
      delta: {
        weight: hasWeightItems
          ? {
              value: weightDelta,
              unit: displayUnit,
              __typename: 'Weight',
            }
          : undefined,
        each: hasEachItems ? eachDelta : undefined,
      },
    };
  }, [
    shippedLineItems,
    receivedLineItems,
    deductions,
    freightCharges,
    weightUnit,
    facilityDefaultUnit,
  ]);
